import { css } from 'styled-components';

// LE SUPERFICI DEL PANNELLO, IN UN POSTO SOLO.
//
// Il backoffice ha **una** scala di colori, chiara, e il tema del marchio ci si compone sopra
// (`CustomStyleApp` → `customThemeProvider`). Una modalità scura non è «gli stessi componenti con
// altri valori»: è una seconda scala, e finché non c'è, ogni colore scritto dentro un componente è
// un posto che qualcuno dovrà riaprire.
//
// Quindi i componenti non nominano più i colori: nominano il **ruolo** che quel colore ha.
//
//     --panel-ground     il fondo su cui poggiano le card
//     --panel-surface    la card
//     --panel-line       ogni bordo e ogni separatore
//     --panel-ink        il testo che si legge — un nome, un valore
//     --panel-ink-muted  il testo che accompagna — un'etichetta, una didascalia
//     --panel-field      il fondo incassato di un campo: è ciò che dice «qui si scrive»
//     --panel-accent     l'azione e lo stato acceso
//     --panel-accent-ink ciò che si posa sopra l'accento
//
// COME SI ACCENDE LA MODALITÀ SCURA, quando sarà il momento: si mette `data-scheme="dark"` sul
// guscio dell'Inspector — o su un suo antenato — e finisce lì. Nessun componente cambia, perché
// nessuno sa di che colore è: sa solo che il suo fondo è la superficie e il suo bordo è la linea.
// Il valore lo prende da `useWorkspacePreferences().colorScheme`, che è la preferenza salvata sul
// server per chi lavora.
//
// I VALORI CHIARI SONO QUELLI DEL TEMA, non copie: se il marchio cambia il proprio grigio, il
// pannello lo segue. Quelli scuri sono scritti qui perché una seconda scala nel tema non esiste —
// e questo file è il posto dove nascerà.
export const scenePanelPalette = css`
    --panel-ground: ${(props) => props.theme.app.pageBackgroundColor};
    --panel-surface: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.backgroundColor};
    --panel-line: ${(props) => props.theme.app.borderColor};
    --panel-ink: ${(props) => props.theme.app.textColor};
    --panel-ink-muted: ${(props) => props.theme.app.primary};
    --panel-field: ${(props) => props.theme.app.backgroundToolbar};
    --panel-accent: ${(props) => props.theme.app.secondary};
    --panel-accent-ink: #ffffff;

    /* LE TRE SPECIE. Non sono i colori semantici: arancio è azione e blu è informazione, e
       prestarli a «questo è un oggetto» li farebbe dire due cose. Il fondo porta la tinta e il
       testo resta neutro, così il contrasto della parola non dipende dalla specie. */
    --panel-kind-subject: #c4e4e4;
    --panel-kind-group: #dfcff9;
    --panel-kind-asset: #f4cadf;
    --panel-kind-light: #f6e3c0;
    --panel-kind-camera: #cfd8ee;

    /* E LA STESSA SPECIE HA UN SECONDO VALORE, SATURO. Il lavato è un FONDO: come colore di una
       parola o di un contorno non si legge. Dove la pastiglia è VUOTA — «4 objects», che conta
       oggetti e ne veste la specie — servono i due valori insieme: il saturo per la parola e il
       contorno, il lavato per il fondo che compare passandoci sopra.
       I VALORI SONO QUELLI DEL PROTOTIPO, non ritinti: il verde-acqua del soggetto, il viola del
       raggruppamento, l'ambra della luce — scelti là per reggere il 3:1 su un glifo da 15px in
       tutti e due i temi (WCAG 1.4.11), che è esattamente il mestiere che fanno anche qui. */
    --panel-kind-subject-ink: #0e7c7c;
    --panel-kind-group-ink: #7040d0;
    --panel-kind-light-ink: #97630f;

    /* La modalità scura è dichiarata e inerte: nessuno scrive ancora questo attributo. Sta qui
       perché il giorno in cui si accende, si accende da un punto solo — e perché un componente
       che nel frattempo nascesse col colore scritto a mano non lo troverebbe mai. */
    &[data-scheme='dark'] {
        --panel-ground: #16191d;
        --panel-surface: #212630;
        --panel-line: #333b46;
        --panel-ink: #e7ebf0;
        --panel-ink-muted: #93a1b0;
        --panel-field: #1a1e25;
        --panel-accent: ${(props) => props.theme.app.secondary};
        --panel-accent-ink: #ffffff;

        /* Non è lo stesso esadecimale scurito: è la stessa tinta ripensata sul fondo scuro. */
        --panel-kind-subject: #154949;
        --panel-kind-group: #352962;
        --panel-kind-asset: #561c3e;
        --panel-kind-light: #4a3617;
        --panel-kind-camera: #242e4c;
        --panel-kind-subject-ink: #3fbdbd;
        --panel-kind-group-ink: #a98bf0;
        --panel-kind-light-ink: #e0a93c;
    }
`;

// I RIPIEGHI SONO I VALORI CHIARI, e non è ridondanza: un componente del pannello può essere
// montato fuori dall'Inspector — la stessa `PropertiesPanel` la usano preview3D, le impostazioni
// interfaccia e due pagine del Virtual Try-On — e lì le variabili non esistono. Senza ripiego
// quelle pagine perderebbero i colori; con il ripiego restano esattamente com'erano.
export const panel = {
    ground: 'var(--panel-ground, #f9f9f9)',
    surface: 'var(--panel-surface, #ffffff)',
    line: 'var(--panel-line, #dbe2e6)',
    ink: 'var(--panel-ink, #263238)',
    inkMuted: 'var(--panel-ink-muted, #455a64)',
    field: 'var(--panel-field, #eceff1)',
    accent: 'var(--panel-accent, #e8650a)',
    accentInk: 'var(--panel-accent-ink, #ffffff)',
    kindSubject: 'var(--panel-kind-subject, #c4e4e4)',
    kindSubjectInk: 'var(--panel-kind-subject-ink, #0e7c7c)',
    kindGroupInk: 'var(--panel-kind-group-ink, #7040d0)',
    kindLightInk: 'var(--panel-kind-light-ink, #97630f)',
    kindGroup: 'var(--panel-kind-group, #dfcff9)',
    kindAsset: 'var(--panel-kind-asset, #f4cadf)',
    kindLight: 'var(--panel-kind-light, #f6e3c0)',
    kindCamera: 'var(--panel-kind-camera, #cfd8ee)'
};
